import React from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    LabelList,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { CATEGORY_LABELS } from '@/shared/constants/listings';
import ChartCard from './ChartCard';
import { money, pounds, whole } from './format';
import { AXIS_TEXT, GRID, NAVY, SLATE } from './palette';

const ROW_HEIGHT = 36;
const CHROME = 48;

/**
 * Pounds diverted by category, heaviest first, categories with no transfers
 * left off the chart. The table lists only those same categories; a category
 * with nothing in it is not a finding.
 */
function CategoryChart({ byCategory }) {
    const rows = Object.entries(byCategory)
        .filter(([, figures]) => figures.transfers > 0)
        .map(([key, figures]) => ({
            key,
            name: CATEGORY_LABELS[key] || key,
            ...figures,
        }))
        .sort((a, b) => b.pounds - a.pounds);

    if (rows.length === 0) {
        return null;
    }

    return (
        <ChartCard
            title="Pounds by category"
            filename="furniture-exchange-pounds-by-category.png"
            summary={
                'Pounds diverted for each category of item that had a ' +
                'transfer, heaviest first.'
            }
            table={{
                corner: 'Category',
                columns: [
                    'Transfers',
                    'Items',
                    'Pounds diverted',
                    'Dollars saved',
                ],
                rows: rows.map((row) => ({
                    key: row.key,
                    label: row.name,
                    values: [
                        whole(row.transfers),
                        whole(row.items),
                        pounds(row.pounds),
                        money(row.dollars),
                    ],
                })),
            }}
        >
            <ResponsiveContainer
                width="100%"
                height={rows.length * ROW_HEIGHT + CHROME}
            >
                <BarChart
                    data={rows}
                    layout="vertical"
                    margin={{ top: 4, right: 48, left: 0, bottom: 4 }}
                    accessibilityLayer={false}
                >
                    <CartesianGrid stroke={GRID} horizontal={false} />
                    <XAxis
                        type="number"
                        tick={AXIS_TEXT}
                        tickFormatter={whole}
                    />
                    <YAxis
                        type="category"
                        dataKey="name"
                        width={112}
                        tick={AXIS_TEXT}
                        interval={0}
                    />
                    <Tooltip formatter={(value) => pounds(value)} />
                    <Bar
                        dataKey="pounds"
                        name="Pounds"
                        fill={SLATE}
                        isAnimationActive={false}
                    >
                        <LabelList
                            dataKey="pounds"
                            position="right"
                            formatter={whole}
                            fill={NAVY}
                            fontSize={11}
                        />
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </ChartCard>
    );
}

export default CategoryChart;
